import { useState } from 'react';

import { Check, ChevronsUpDown, Layers } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { PermissionCheck } from '@/components/permissionCheck';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Text } from '@/components/ui/typography';
import { useCanEnterSandbox } from '@/hooks/useCanEnterSandbox';
import { useCurrentSandbox } from '@/hooks/useCurrentSandbox';
import { useSandboxSwitch } from '@/hooks/useGameSandboxSwitch';
import { Permissions } from '@/models/permission';
import { useSandboxContext } from '@/providers';

import { SandboxExportItem } from './sandboxExportItem';

export const SandboxBreadcrumbDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);

  // The one source for the scope: the provider the breadcrumb renders under.
  const { selectedSandbox, gameCode } = useSandboxContext();
  const { studioCode = '' } = useParams<{ studioCode: string }>();
  const navigate = useNavigate();

  const { switchSandbox } = useSandboxSwitch({
    onBeforeSwitch: () => {
      setIsOpen(false);
    },
  });

  const { current: currentSandbox, others } = useCurrentSandbox(
    gameCode,
    selectedSandbox,
  );

  const canEnterSandbox = useCanEnterSandbox();

  const handleSandboxChange = (newSandbox: string) => {
    switchSandbox(studioCode, gameCode, selectedSandbox, newSandbox);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {/* The whole crumb is the trigger, labelled: users read a bare name beside a
            small icon as plain text and never found the switch. */}
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 px-2"
          title="Switch sandbox"
        >
          <Text variant="caption">Sandbox</Text>
          <span className="truncate max-w-48 text-foreground">
            {currentSandbox?.name ?? selectedSandbox}
          </span>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-xs p-0">
        <Command>
          <CommandInput placeholder="Search sandbox..." />
          <CommandList className="max-h-48 overflow-y-auto">
            <CommandEmpty>No sandbox found.</CommandEmpty>
            {currentSandbox && (
              <>
                <CommandGroup heading="Current">
                  <CommandItem
                    value={currentSandbox.name}
                    onSelect={() => {
                      handleSandboxChange(currentSandbox.code);
                    }}
                  >
                    <Check className="mr-2 h-4 w-4 opacity-100" />
                    {currentSandbox.name}
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading="All sandboxes">
              {others.map((sandbox) => (
                <CommandItem
                  key={sandbox.code}
                  value={sandbox.code}
                  // Same rule as the Sandbox list: a Sandbox no Grant covers
                  // stays visible but cannot be switched to from here either.
                  disabled={!canEnterSandbox(sandbox.code)}
                  onSelect={() => {
                    handleSandboxChange(sandbox.code);
                  }}
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  {sandbox.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          <CommandSeparator />
          <CommandGroup className="p-1">
            {/* The way back to the game's Sandbox list. Not the game URL: its index
                  redirects to the last saved Sandbox. */}
            <CommandItem
              onSelect={() => {
                setIsOpen(false);
                navigate(`/studios/${studioCode}/${gameCode}/sandboxes`);
              }}
            >
              <Layers className="mr-2 h-4 w-4" />
              All sandboxes
            </CommandItem>
          </CommandGroup>
          <PermissionCheck oneOf={[Permissions.sandboxes.export]}>
            <CommandSeparator />
            <CommandGroup className="p-1">
              <SandboxExportItem
                sandbox={currentSandbox}
                onExported={() => {
                  setIsOpen(false);
                }}
              />
            </CommandGroup>
          </PermissionCheck>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
